<template>
  <li class="nav-item sidenav-group mb-1">
    <button
      type="button"
      class="sidenav-group__btn"
      :class="{ 'is-open': isOpen, 'is-active': hasActiveChild }"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggle"
    >
      <span class="sidenav-group__left">
        <i v-if="icon" class="material-icons-round sidenav-group__icon">{{ icon }}</i>
        <span class="sidenav-group__label">{{ label }}</span>
      </span>
      <i class="material-icons-round sidenav-group__chevron">expand_more</i>
    </button>
    <div v-show="isOpen" class="sidenav-group__body">
      <ul class="nav flex-column sidenav-group__list">
        <slot />
      </ul>
    </div>
  </li>
</template>

<script>
export default {
  name: "SidenavGroup",
  inject: {
    getSidenavOpenGroup: { default: null },
    setSidenavOpenGroup: { default: null },
  },
  props: {
    groupId: { type: String, required: true },
    label: { type: String, required: true },
    icon: { type: String, default: "" },
    routes: {
      type: Array,
      default: () => [],
    },
    defaultOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localOpen: this.defaultOpen,
    };
  },
  computed: {
    isOpen() {
      if (typeof this.getSidenavOpenGroup === "function") {
        return this.getSidenavOpenGroup() === this.groupId;
      }
      return this.localOpen;
    },
    hasActiveChild() {
      const path = this.$route.path || "";
      const query = this.$route.query || {};
      return this.routes.some((r) => {
        if (!r) return false;
        if (typeof r === "object") {
          if (!(path === r.path || path.startsWith(r.path + "/"))) return false;
          if (r.query) {
            return Object.keys(r.query).every(
              (k) => String(query[k] || "") === String(r.query[k])
            );
          }
          if (r.excludeQuery) {
            return Object.keys(r.excludeQuery).every(
              (k) => String(query[k] || "") !== String(r.excludeQuery[k])
            );
          }
          return true;
        }
        if (r === "/") return path === "/";
        return path === r || path.startsWith(r + "/");
      });
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        if (this.hasActiveChild) this.openSelf();
      },
    },
  },
  mounted() {
    if (
      this.defaultOpen &&
      typeof this.getSidenavOpenGroup === "function" &&
      !this.getSidenavOpenGroup()
    ) {
      this.openSelf();
    }
  },
  methods: {
    openSelf() {
      if (typeof this.setSidenavOpenGroup === "function") {
        this.setSidenavOpenGroup(this.groupId);
      } else {
        this.localOpen = true;
      }
    },
    toggle() {
      if (typeof this.setSidenavOpenGroup === "function") {
        this.setSidenavOpenGroup(this.isOpen ? null : this.groupId);
      } else {
        this.localOpen = !this.localOpen;
      }
    },
  },
};
</script>

<style scoped>
.sidenav-group__btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 12px;
  padding: 0.55rem 0.75rem;
  color: #344767;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
}

.sidenav-group__btn:hover {
  background: #f3eefb;
  color: #5e29b0;
}

.sidenav-group__btn:hover .sidenav-group__icon,
.sidenav-group__btn:hover .sidenav-group__chevron {
  color: #5e29b0;
  opacity: 1;
}

.sidenav-group__btn.is-open {
  background: rgba(94, 41, 176, 0.12) !important;
  border-color: rgba(94, 41, 176, 0.28);
  color: #5e29b0 !important;
  box-shadow: none !important;
}

.sidenav-group__btn.is-open .sidenav-group__label,
.sidenav-group__btn.is-open .sidenav-group__icon,
.sidenav-group__btn.is-open .sidenav-group__chevron {
  color: #5e29b0 !important;
  opacity: 1;
}

.sidenav-group__btn.is-open .sidenav-group__label,
.sidenav-group__btn.is-open .sidenav-group__icon,
.sidenav-group__btn.is-open .sidenav-group__chevron {
  color: #5e29b0 !important;
  opacity: 1;
}

.sidenav-group__btn.is-active:not(.is-open) {
  background: #f4f5f7;
  border-color: transparent;
}

.sidenav-group__left {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.sidenav-group__icon {
  font-size: 1.1rem;
  opacity: 0.7;
}

.sidenav-group__label {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.sidenav-group__chevron {
  font-size: 1.1rem;
  opacity: 0.5;
  transition: transform 0.18s ease;
}

.sidenav-group__btn.is-open .sidenav-group__chevron {
  transform: rotate(180deg);
}

.sidenav-group__body {
  padding: 0.35rem 0 0.2rem;
  margin-top: 0.15rem;
}

.sidenav-group__list {
  margin: 0;
  padding: 0.2rem 0.15rem 0.15rem 0.35rem;
  list-style: none;
  background: #f8f7fb;
  border-radius: 10px;
}

.sidenav-group__list :deep(.nav-item) {
  margin-bottom: 0.05rem;
}

.sidenav-group__list :deep(.nav-link) {
  border-radius: 10px !important;
  padding: 0.4rem 0.7rem !important;
  min-height: auto !important;
  margin: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  color: #344767 !important;
}

.sidenav-group__list :deep(.nav-link .material-icons-round),
.sidenav-group__list :deep(.nav-link .material-icons) {
  color: #67748e !important;
  opacity: 1 !important;
}

.sidenav-group__list :deep(.nav-link-text),
.sidenav-group__list :deep(.sidenav-normal) {
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  color: #344767 !important;
  opacity: 1 !important;
}

.sidenav-group__list :deep(.nav-link.active),
.sidenav-group__list :deep(.nav-link.active.bg-gradient-primary),
.sidenav-group__list :deep(.nav-link[class*="bg-gradient-"]) {
  background: #ebe4f7 !important;
  box-shadow: none !important;
  color: #5e29b0 !important;
}

.sidenav-group__list :deep(.nav-link.active .nav-link-text),
.sidenav-group__list :deep(.nav-link.active .sidenav-normal),
.sidenav-group__list :deep(.nav-link.active .material-icons-round),
.sidenav-group__list :deep(.nav-link.active .material-icons) {
  color: #5e29b0 !important;
}
</style>

<style>
/* Dark sidenav (not light sidebar): readable group chrome */
body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn {
  color: rgba(255, 255, 255, 0.82);
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn:hover {
  background: rgba(124, 58, 237, 0.22);
  color: #fff;
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn.is-open {
  background: rgba(124, 58, 237, 0.28) !important;
  border-color: rgba(167, 139, 250, 0.45);
  color: #fff !important;
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn.is-open .sidenav-group__label,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn.is-open .sidenav-group__icon,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn.is-open .sidenav-group__chevron {
  color: #fff !important;
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn:hover .sidenav-group__icon,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn:hover .sidenav-group__chevron {
  color: #fff;
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__btn.is-active:not(.is-open) {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__list {
  background: rgba(0, 0, 0, 0.22);
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link-text,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .sidenav-normal,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .material-icons-round,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .material-icons {
  color: rgba(255, 255, 255, 0.78) !important;
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link.active,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link.active.bg-gradient-primary,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link[class*="bg-gradient-"] {
  background: rgba(124, 58, 237, 0.35) !important;
  color: #fff !important;
}

body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link.active .nav-link-text,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link.active .sidenav-normal,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link.active .material-icons-round,
body.dark-version .sidenav:not(.bg-white) .sidenav-group__list .nav-link.active .material-icons {
  color: #fff !important;
}

/* Dark mode + light sidebar: keep Soft UI light group colors */
body.dark-version .sidenav.bg-white .sidenav-group__btn {
  color: #344767;
}

body.dark-version .sidenav.bg-white .sidenav-group__list {
  background: #f8f7fb;
}

body.dark-version .sidenav.bg-white .sidenav-group__btn.is-open {
  background: rgba(94, 41, 176, 0.12) !important;
  border-color: rgba(94, 41, 176, 0.28);
  color: #5e29b0 !important;
}

body.dark-version .sidenav.bg-white .sidenav-group__btn.is-open .sidenav-group__label,
body.dark-version .sidenav.bg-white .sidenav-group__btn.is-open .sidenav-group__icon,
body.dark-version .sidenav.bg-white .sidenav-group__btn.is-open .sidenav-group__chevron {
  color: #5e29b0 !important;
}
</style>
